// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { ApiError, apiFetch, describeApiError } from "@/lib/api";

// Backend-facing operational secrets — service-to-service credentials
// the backend itself reads to talk to other internal services.
//
// Initial slot: backend_mcp_bearer (the bearer the backend uses to
// call dev-mcp.buddymedia.online). Mint at /admin/operations/service-accounts
// (T-019), store the value here so rotations don't require touching
// the container env on every cluster member.
//
// Resolution order at the backend: env var BW_BACKEND_MCP_BEARER first
// (legacy bootstrap), then this column. If neither is set, the digest
// admin proxy returns 503 with mint instructions.

type MergeDaemonBearerStatus = {
  configured: boolean;
  last_rotated_at: string | null;
  last_rotated_by_user_id: number | null;
  bearer_jti: string | null;
};

type OperationalSecretsOut = {
  backend_mcp_bearer_configured: boolean;
  code_worker_1_bearer_configured: boolean;
  code_worker_2_bearer_configured: boolean;
  cluster_github_pat_configured: boolean;
  merge_daemon_mcp_bearer: MergeDaemonBearerStatus;
};

type SecretField = {
  key: keyof OperationalSecretsUpdate;
  label: string;
  description: string;
  configuredKey: keyof OperationalSecretsOut;
  placeholder: string;
};

type OperationalSecretsUpdate = {
  backend_mcp_bearer?: string;
  code_worker_1_bearer?: string;
  code_worker_2_bearer?: string;
  cluster_github_pat?: string;
};

type MergeDaemonMintResponse = {
  service_account_id: string;
  bearer_jti: string;
  expires_at: string;
  revoked_prior_bearers: number;
  last_rotated_at: string;
  notice: string;
};

const SECRET_FIELDS: SecretField[] = [
  {
    key: "backend_mcp_bearer",
    label: "Backend → MCP bearer",
    description:
      "Service-account JWT the backend uses to call dev-mcp.buddymedia.online. Mint at /admin/operations/service-accounts under name 'backend-proxy', then paste the value here. Empty submission clears.",
    configuredKey: "backend_mcp_bearer_configured",
    placeholder: "eyJ… (bearer JWT from a service-account mint)",
  },
  {
    key: "code_worker_1_bearer",
    label: "frontend-worker MCP bearer",
    description:
      "Bearer frontend-worker (formerly frontend-worker) uses to authenticate to dev-mcp.buddymedia.online. Mint at /admin/operations/service-accounts under name 'frontend-worker' (or legacy 'frontend-worker' — both resolve via T-336 KNOWN_WORKERS) and paste the value here to land it in the admin-managed slot. Worker-side self-fetch (so the value here actually rotates the live worker) is T-022 (self-healing MCP); until then this slot is staging — the worker still reads BW_MCP_BEARER from /root/.buddywatch/orchestration.env on its host LXC. Backend storage column retains the legacy code_worker_1_bearer name.",
    configuredKey: "code_worker_1_bearer_configured",
    placeholder: "eyJ… (bearer JWT minted for frontend-worker)",
  },
  {
    key: "code_worker_2_bearer",
    label: "backend-worker MCP bearer",
    description:
      "Bearer backend-worker (formerly backend-worker) uses to authenticate to dev-mcp.buddymedia.online. Mint at /admin/operations/service-accounts under name 'backend-worker' (or legacy 'backend-worker') and paste the value here. Same staging caveat as the frontend-worker slot — T-022 wires the worker self-fetch. Backend storage column retains the legacy code_worker_2_bearer name.",
    configuredKey: "code_worker_2_bearer_configured",
    placeholder: "eyJ… (bearer JWT minted for backend-worker)",
  },
  {
    key: "cluster_github_pat",
    label: "Cluster GitHub PAT (gh on workers)",
    description:
      "Shared GitHub Personal Access Token used by `gh` on all Code workers for PR create / merge / list across the post-split repos (dev-buddywatch-backend, dev-buddywatch-frontend-desktop, dev-buddywatch-landing, dev-buddywatch-docs, buddynet). Org-scope PAT — single slot covers all repos. Generate at github.com/settings/tokens (fine-grained PAT recommended; scopes: Pull requests Read & write, Contents Read & write, Metadata Read; restrict to those five repos). Workers fetch the decrypted value via scripts/orchestration/fetch-github-pat.sh — no SSH-into-LXC, no chat exposure, no env-file edits. Rotate by pasting a new PAT; takes effect on next worker fetch.",
    configuredKey: "cluster_github_pat_configured",
    placeholder: "github_pat_… or ghp_… (paste the PAT value)",
  },
];

export function OperationalSecretsPage() {
  const [state, setState] = useState<OperationalSecretsOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    apiFetch<OperationalSecretsOut>(
      "/api/admin/system-settings/operational-secrets",
    )
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `Failed to load (HTTP ${err.status})`
            : "Failed to load operational secrets",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return (
    <AdminPageShell
      title="Operational secrets"
      description="Backend-facing service-to-service credentials. Distinct from email/storage credentials (those are managed under their own surfaces) and from per-user secrets (OAuth tokens, MFA factors). Stored encrypted at rest."
    >
      <div className="space-y-6">
        {error ? (
          <AdminCard className="border-rose-900/40 bg-rose-950/20">
            <p className="text-sm text-rose-200">{error}</p>
          </AdminCard>
        ) : null}

        {state === null ? (
          <AdminCard>
            <p className="text-sm text-slate-400">Loading…</p>
          </AdminCard>
        ) : (
          <div className="space-y-4">
            {SECRET_FIELDS.map((field) => (
              <SecretRow
                key={field.key as string}
                field={field}
                configured={
                  state[field.configuredKey] as boolean
                }
                onUpdated={() => setRefreshKey((k) => k + 1)}
              />
            ))}
            <MergeDaemonBearerRow
              status={state.merge_daemon_mcp_bearer}
              onUpdated={() => setRefreshKey((k) => k + 1)}
            />
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}

function SecretRow({
  field,
  configured,
  onUpdated,
}: {
  field: SecretField;
  configured: boolean;
  onUpdated: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [savedJustNow, setSavedJustNow] = useState(false);

  const submit = async (clearInstead = false) => {
    setBusy(true);
    setRowError(null);
    try {
      const payload: OperationalSecretsUpdate = {
        [field.key]: clearInstead ? "" : value,
      } as OperationalSecretsUpdate;
      await apiFetch<OperationalSecretsOut>(
        "/api/admin/system-settings/operational-secrets",
        { method: "PUT", body: JSON.stringify(payload) },
      );
      setValue("");
      setEditing(false);
      setSavedJustNow(true);
      setTimeout(() => setSavedJustNow(false), 4000);
      onUpdated();
    } catch (err) {
      setRowError(describeApiError(err, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide ${
            configured
              ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
              : "border-amber-500/30 bg-amber-500/15 text-amber-300"
          }`}
        >
          {configured ? "Configured" : "Not set"}
        </span>
        <span className="font-semibold text-white">{field.label}</span>
        {savedJustNow ? (
          <span className="ml-auto text-xs text-emerald-400">Saved.</span>
        ) : null}
      </div>
      <p className="text-xs text-slate-400">{field.description}</p>

      {rowError ? <p className="text-xs text-rose-300">{rowError}</p> : null}

      {!editing ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white"
          >
            {configured ? "Replace value" : "Set value"}
          </button>
          {configured ? (
            <button
              type="button"
              onClick={() => {
                if (
                  confirm(
                    `Clear ${field.label}? Whatever consumer reads this slot will fall through to its env-var fallback (or fail if there isn't one).`,
                  )
                ) {
                  submit(true);
                }
              }}
              disabled={busy}
              className="rounded-md border border-rose-700 px-3 py-1.5 text-xs font-medium text-rose-300 hover:bg-rose-950/40 disabled:opacity-50"
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 font-mono text-xs text-white placeholder-slate-600"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={busy || !value.trim()}
              className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setValue("");
                setRowError(null);
              }}
              disabled={busy}
              className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-700 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <span className="text-[11px] text-slate-500">
              Value is shown only while editing. Backend redacts on read.
            </span>
          </div>
        </div>
      )}
    </AdminCard>
  );
}

// ── Merge daemon MCP bearer (T-156 / Rule J: secrets-only-via-admin-stores) ──
//
// Closes the bootstrap gap T-153 left open. The merge daemon at
// scripts/orchestration/merge-daemon.sh on buddywatch-dev fetches its MCP
// bearer per-tick from /api/internal/merge-daemon-bearer (X-Bootstrap-
// Key gated). NEVER pasted into an env file; rotation is fully
// online (next daemon tick within ≤60s picks up the new value).
//
// Two flows:
//   - "Mint + install" — single click; backend mints with the
//     merge-daemon service account, encrypts, stores. Plaintext
//     bearer NEVER returns to the UI (by design).
//   - "Paste existing" — for migration scenarios where an admin has
//     a pre-existing bearer string. Same no-display-after-save
//     discipline.

function relTime(iso: string | null): string {
  if (!iso) return "never";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "never";
  const deltaSec = Math.max(0, (Date.now() - then) / 1000);
  if (deltaSec < 60) return "just now";
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  if (deltaSec < 86400 * 30) return `${Math.floor(deltaSec / 86400)}d ago`;
  return new Date(then).toISOString().slice(0, 10);
}

function MergeDaemonBearerRow({
  status,
  onUpdated,
}: {
  status: MergeDaemonBearerStatus;
  onUpdated: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteValue, setPasteValue] = useState("");

  const mintAndInstall = async () => {
    if (busy) return;
    if (
      !confirm(
        "Mint a fresh merge-daemon MCP bearer and install it?\n\n" +
          "This will REVOKE any prior un-revoked bearers for the merge-" +
          "daemon service account, mint a new one, and store it encrypted. " +
          "The daemon will pick it up on its next tick (≤60s).\n\n" +
          "The bearer value is NOT displayed — by design (Rule J: " +
          "secrets-only-via-admin-stores).",
      )
    ) {
      return;
    }
    setBusy(true);
    setRowError(null);
    setSuccessNotice(null);
    try {
      const resp = await apiFetch<MergeDaemonMintResponse>(
        "/api/admin/system-settings/operational-secrets/merge-daemon-bearer/mint",
        { method: "POST", body: "{}" },
      );
      const revokedNote =
        resp.revoked_prior_bearers > 0
          ? ` Revoked ${resp.revoked_prior_bearers} prior bearer(s).`
          : "";
      setSuccessNotice(
        `${resp.notice}${revokedNote} (jti: ${resp.bearer_jti.slice(0, 8)}…, expires ${new Date(resp.expires_at).toISOString().slice(0, 10)})`,
      );
      onUpdated();
    } catch (err) {
      setRowError(describeApiError(err, "Mint failed"));
    } finally {
      setBusy(false);
    }
  };

  const pasteExisting = async () => {
    if (busy || !pasteValue.trim()) return;
    setBusy(true);
    setRowError(null);
    setSuccessNotice(null);
    try {
      await apiFetch<OperationalSecretsOut>(
        "/api/admin/system-settings/operational-secrets",
        {
          method: "PUT",
          body: JSON.stringify({ merge_daemon_mcp_bearer: pasteValue }),
        },
      );
      setPasteValue("");
      setPasteMode(false);
      setSuccessNotice(
        "Existing bearer encrypted + stored. Daemon picks it up on its next tick (≤60s).",
      );
      onUpdated();
    } catch (err) {
      setRowError(describeApiError(err, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const clearSlot = async () => {
    if (
      !confirm(
        "Clear the merge-daemon bearer slot? The daemon will lose MCP " +
          "auth on its next tick and start emitting 401s until a new " +
          "bearer is minted.",
      )
    ) {
      return;
    }
    setBusy(true);
    setRowError(null);
    setSuccessNotice(null);
    try {
      await apiFetch<OperationalSecretsOut>(
        "/api/admin/system-settings/operational-secrets",
        {
          method: "PUT",
          body: JSON.stringify({ merge_daemon_mcp_bearer: "" }),
        },
      );
      setSuccessNotice("Slot cleared.");
      onUpdated();
    } catch (err) {
      setRowError(describeApiError(err, "Clear failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard className="space-y-3 border-emerald-900/40">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide ${
            status.configured
              ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
              : "border-amber-500/30 bg-amber-500/15 text-amber-300"
          }`}
        >
          {status.configured ? "Configured" : "Not configured"}
        </span>
        <span className="font-semibold text-white">
          Merge daemon MCP bearer
        </span>
        {status.configured && status.last_rotated_at ? (
          <span className="ml-auto text-xs text-slate-400">
            Rotated {relTime(status.last_rotated_at)}
            {status.last_rotated_by_user_id !== null
              ? ` by user #${status.last_rotated_by_user_id}`
              : ""}
          </span>
        ) : null}
      </div>
      <p className="text-xs text-slate-400">
        The merge daemon (
        <code className="text-slate-300">scripts/orchestration/merge-daemon.sh</code>{" "}
        on buddywatch-dev) authenticates to MCP with this bearer to write task
        statuses + emit q_stream events. The daemon fetches it per-tick
        from <code>/api/internal/merge-daemon-bearer</code> — rotation
        is online (≤60s pickup). Status surface at{" "}
        <a
          href="/admin/operations/merge-daemon"
          className="text-pink-400 hover:underline"
        >
          /admin/operations/merge-daemon
        </a>
        .
      </p>
      <p className="text-[11px] italic text-slate-500">
        Per Rule J (<code>secrets-only-via-admin-stores</code>): the
        bearer NEVER touches an env file or chat. Mint here; the daemon
        reads the encrypted slot. The plaintext value is not displayed
        post-mint.
      </p>

      {rowError ? <p className="text-xs text-rose-300">{rowError}</p> : null}
      {successNotice ? (
        <p className="text-xs text-emerald-300">{successNotice}</p>
      ) : null}

      {!pasteMode ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={mintAndInstall}
            disabled={busy}
            className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            {busy ? "Working…" : status.configured ? "Re-mint + install" : "Mint + install"}
          </button>
          <button
            type="button"
            onClick={() => {
              setPasteMode(true);
              setRowError(null);
              setSuccessNotice(null);
            }}
            disabled={busy}
            className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-200 hover:border-slate-600 disabled:opacity-50"
          >
            Paste existing…
          </button>
          {status.configured ? (
            <button
              type="button"
              onClick={clearSlot}
              disabled={busy}
              className="ml-auto rounded-md border border-rose-700 px-3 py-1.5 text-xs font-medium text-rose-300 hover:bg-rose-950/40 disabled:opacity-50"
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          <textarea
            value={pasteValue}
            onChange={(e) => setPasteValue(e.target.value)}
            placeholder="eyJ… (existing service-account JWT minted for merge-daemon)"
            rows={4}
            className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 font-mono text-xs text-white placeholder-slate-600"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={pasteExisting}
              disabled={busy || !pasteValue.trim()}
              className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save bearer"}
            </button>
            <button
              type="button"
              onClick={() => {
                setPasteMode(false);
                setPasteValue("");
                setRowError(null);
              }}
              disabled={busy}
              className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-700 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <span className="text-[11px] text-slate-500">
              Value is shown only while editing. Backend redacts on read.
            </span>
          </div>
        </div>
      )}
    </AdminCard>
  );
}
